import {
  normalizeLandRequestStage,
  postSubmitTimelineState,
  POST_SUBMIT_TIMELINE_STEPS,
  type LandRequestStageCategory,
} from "../../../../utils/donationRequestUiStages";

type Props = {
  stageName?: string | null;
  state?: string | null;
};

function outcomeClass(cat: LandRequestStageCategory, stepIndex: number, currentIndex: number): string {
  if (stepIndex !== currentIndex || stepIndex !== 3) return "";
  if (cat === "approved") return "ring-2 ring-emerald-300/80 bg-emerald-50/90";
  if (cat === "rejected") return "ring-2 ring-red-300/80 bg-red-50/90";
  return "";
}

function TimelineDot({
  done,
  current,
  index,
}: {
  done: boolean;
  current: boolean;
  index: number;
}) {
  return (
    <span className="relative mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center" aria-hidden>
      {current && (
        <span className="absolute -inset-1 rounded-full bg-sa-400/25 animate-pulse" />
      )}
      <span
        className={`relative flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold tabular-nums transition-all duration-300 ${
          done
            ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25"
            : current
              ? "bg-sa-600 text-white shadow-lg shadow-sa-600/30 ring-2 ring-white ring-offset-2 ring-offset-sa-100/50"
              : "border border-neutral-200 bg-white text-neutral-400"
        }`}
      >
        {done ? (
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        ) : (
          index + 1
        )}
      </span>
    </span>
  );
}

export default function LandDonationPostSubmitTimeline({ stageName, state }: Props) {
  const cat = normalizeLandRequestStage(stageName, state);
  const { currentIndex } = postSubmitTimelineState(cat);

  const cardClass = (i: number, done: boolean, current: boolean, pending: boolean, extra: string) =>
    `rounded-2xl border px-3 py-3 text-right transition-all duration-300 lg:py-2.5 ${
      pending
        ? "border-dashed border-neutral-200/90 bg-white/50 text-neutral-400"
        : done
          ? "border-emerald-200/90 bg-emerald-50/70 text-emerald-950 shadow-sm shadow-emerald-900/5"
          : current
            ? `border-sa-300/90 bg-sa-50/90 text-sa-950 shadow-md shadow-sa-900/5 ${extra}`
            : "border-neutral-200 bg-white"
    }`;

  return (
    <div
      className="rounded-2xl border border-neutral-200/80 bg-gradient-to-br from-neutral-50/95 via-white to-sa-50/20 p-4 shadow-lg shadow-neutral-200/30 md:p-5"
      dir="rtl"
    >
      {/* Mobile + tablet: vertical timeline */}
      <ol className="relative space-y-0 lg:hidden">
        {POST_SUBMIT_TIMELINE_STEPS.map((step, i) => {
          const done = i < currentIndex;
          const current = i === currentIndex;
          const pending = i > currentIndex;
          const extra = outcomeClass(cat, i, currentIndex);
          const showRail = i < POST_SUBMIT_TIMELINE_STEPS.length - 1;

          return (
            <li key={step.key} className="relative flex gap-3">
              <div className="flex flex-col items-center">
                <TimelineDot done={done} current={current} index={i} />
                {showRail && (
                  <div
                    className={`my-0.5 min-h-[36px] w-0.5 flex-1 rounded-full ${
                      done ? "bg-gradient-to-b from-emerald-500 to-emerald-300" : "bg-neutral-200"
                    }`}
                    aria-hidden
                  />
                )}
              </div>
              <div
                className={`${cardClass(i, done, current, pending, extra)} mb-3 min-h-[3.5rem] flex-1 last:mb-0`}
              >
                <span className={`text-sm font-semibold leading-snug ${pending ? "text-neutral-400" : ""}`}>
                  {step.label}
                </span>
                {current && i === 3 && cat === "approved" && (
                  <span className="mt-1.5 block text-xs font-medium text-emerald-800">تمت الموافقة</span>
                )}
                {current && i === 3 && cat === "rejected" && (
                  <span className="mt-1.5 block text-xs font-medium text-red-800">غير معتمد</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Desktop: horizontal cards */}
      <ol className="hidden gap-3 lg:grid lg:grid-cols-4">
        {POST_SUBMIT_TIMELINE_STEPS.map((step, i) => {
          const done = i < currentIndex;
          const current = i === currentIndex;
          const pending = i > currentIndex;
          const extra = outcomeClass(cat, i, currentIndex);

          return (
            <li
              key={`desktop-${step.key}`}
              className={`${cardClass(i, done, current, pending, extra)} flex flex-col justify-between`}
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className={`text-sm font-semibold leading-snug ${pending ? "text-neutral-400" : ""}`}>
                  {step.label}
                </span>
                <span className="relative flex h-7 w-7 shrink-0 items-center justify-center" aria-hidden>
                  {current && (
                    <span className="absolute -inset-0.5 rounded-full bg-sa-400/20 animate-pulse" />
                  )}
                  <span
                    className={`relative flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold tabular-nums ${
                      done
                        ? "bg-emerald-600 text-white"
                        : current
                          ? "bg-sa-600 text-white ring-2 ring-white ring-offset-1 ring-offset-sa-50"
                          : "border border-neutral-200 bg-neutral-100 text-neutral-400"
                    }`}
                  >
                    {done ? "✓" : i + 1}
                  </span>
                </span>
              </div>
              {current && i === 3 && cat === "approved" && (
                <span className="text-xs font-medium text-emerald-800">تمت الموافقة</span>
              )}
              {current && i === 3 && cat === "rejected" && (
                <span className="text-xs font-medium text-red-800">غير معتمد</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
